/**
 * Retry utility with exponential backoff
 * Matches mobile app retry pattern from useVideoUpload.ts
 */

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s

/**
 * Retry an async operation with exponential backoff
 *
 * @param operation - The async function to retry
 * @param retryCount - Current retry attempt (internal, starts at 0)
 * @returns Promise resolving to the operation's result
 *
 * @example
 * const result = await retryOperation(() => uploadToGCS(file, url));
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  retryCount = 0
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      const delay = RETRY_DELAYS[retryCount];
      console.log(`[Retry] Attempt ${retryCount + 1}/${MAX_RETRIES} after ${delay}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return retryOperation(operation, retryCount + 1);
    }
    throw error;
  }
}
